<template>
  <tag-select v-if="taggable" v-bind="selectProps" @change="onChange" />
  <multi-select v-else-if="multiple" v-bind="selectProps" @change="onChange" />
  <single-select v-else v-bind="selectProps" @change="onChange" />
</template>

<script>
import MultiSelect from './MultiSelect.vue'
import SingleSelect from './SingleSelect.vue'
import TagSelect from './TagSelect.vue'

import { get, isEmpty, pickBy } from 'lodash'
import { convertOptions } from '../../../utils'
import notification from '../../../utils/mixin-notify'

/**
 * @class BaseDatalist
 *
 * @description This component renders a dropdownlist
 */
export default {
  name: 'BaseDatalist',
  components: {
    SingleSelect,
    MultiSelect,
    TagSelect,
  },
  mixins: [notification],
  props: {
    id: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
    },
    value: {
      type: [Array, Number, String],
      default: '',
    },
    defaultValue: {
      type: [Array, Number, String],
    },
    list: {
      type: [Array, Object, String],
      default: () => [],
    },
    listFilterId: {
      type: String,
      required: false,
    },
    minimumInputLength: {
      type: Number,
      default: 1,
    },
    isInvalid: {
      type: Boolean,
      default: false,
    },
    idForm: {
      type: String,
      default: '',
    },
    idQuery: {
      type: String,
      default: '',
    },
    listExp: {
      type: Object,
      default: () => ({}),
    },
    fieldName: {
      type: String,
      default: null,
    },
    listValueURL: {
      type: String,
      default: 'Dynform_listValue',
    },
    fields: {
      type: Array,
      default: () => [],
    },
    field: {
      type: Object,
      default: () => ({}),
    },
    groupReadOnly: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
    },
    readOnly: {
      type: [Boolean, Number],
      default: false,
    },
    dropdownOnlyOne: {
      type: Boolean,
      default: false,
    },
    componentId: {
      type: String,
      default: '',
    },
    localizeData: {
      type: Boolean,
      default: false,
    },
    isBooleanList: {
      type: Boolean,
      default: false,
    },
    path: {
      type: String,
    },
    formName: {
      type: String,
    },
  },
  methods: {
    onChange({ value, options, byExpression }) {
      this.$emit('change', { value, options })
      this.$emit('form-item-changed', { name: this.name, value, options, byExpression })
    },
    setDefaultValue() {
      this.$emit('change', { value: this.defaultValue })
      this.$emit('form-item-changed', {
        name: this.name,
        value: this.defaultValue,
        options: this.options,
        byExpression: true,
      })
    },
  },
  computed: {
    computedField() {
      if (!isEmpty(this.field)) return this.field
      return pickBy({
        id: this.id,
        listExp: this.listExp,
        name: this.name,
        value: this.value,
      })
    },
    hasKeyAsString() {
      return get(this.field, 'neededBecauseWeWereMorons', false)
    },
    optionRenderLimit() {
      return get(this.$CORE, 'env.optionRenderLimit')
    },
    selectProps() {
      return {
        field: this.computedField,
        fieldNameProp: this.fieldName,
        computedReadOnly: this.computedReadOnly,
        idForm: this.idForm,
        idQuery: this.idQuery,
        isInvalid: this.isInvalid,
        list: {
          options: convertOptions(this.$i18n, get(this.list, 'options', []), this.localizeData),
          completed: get(this.list, 'completed', false),
        },
        path: this.path,
        taggable: this.taggable,
        multiple: this.multiple,
        clearable: this.clearable,
        dropdownOnlyOne: this.dropdownOnlyOne,
        localizeData: this.localizeData,
        isBooleanList: this.isBooleanList,
        hasKeyAsString: this.hasKeyAsString,
        formName: this.formName,
      }
    },
    computedReadOnly() {
      return !!(this.readOnly || this.groupReadOnly || this.disabled)
    },
    listId() {
      return `list-${this.id || this.$uid(this.name)}`
    },
  },
  mounted() {
    !this.value && this.defaultValue && this.setDefaultValue()
  },
}
</script>

<style lang="scss" scoped>
@import '../../../assets/sass/variables';
.placeholder {
  color: #c0c0c0;
  font-weight: normal;
}

.readonly {
  background: $background-color-readonly !important;
  cursor:
    url('../../../assets/img/notallowed24.png') 2 2,
    not-allowed !important;
}

.readonly.el-input__inner {
  min-height: 40px;
  height: auto;
}

.fa-spin {
  line-height: 0 !important;
}

.datalist__list {
  padding-top: 0;
}

.flex-fill .el-select {
  display: flex;
}

.el-select.stretch {
  width: 100%;
}
</style>
